import React, { useState, useEffect, useContext } from 'react';
import Header from '../../components/Header';
import { db } from '../../services/firebaseConnection';
import { collection, getDocs, doc, getDoc, deleteDoc } from 'firebase/firestore';
import { Link } from 'react-router-dom';
import { FiEdit2, FiPlus } from 'react-icons/fi';
import { FaAddressCard } from 'react-icons/fa';
import { FaRegTrashAlt } from "react-icons/fa";
import { toast } from 'react-toastify';
import { AuthContext } from '../../contexts/auth';

export default function User() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'ascending' });
  const { user } = useContext(AuthContext);
  const [permissions, setPermissions] = useState([]);

  // States for filtering
  const [filterGenero, setFilterGenero] = useState('');
  const [filterCargo, setFilterCargo] = useState('');
  const [filterCidade, setFilterCidade] = useState('');
  const [filterIgreja, setFilterIgreja] = useState('');

  useEffect(() => {
    const getUserFromDatabase = async () => {
      try {
        const userSnapshot = await getDocs(collection(db, 'User'));
        const userData = await Promise.all(
          userSnapshot.docs.map(async docSnapshot => {
            const user = { id: docSnapshot.id, ...docSnapshot.data() };

            if (user.cidade) {
              const cidadeRef = doc(db, 'Cidade', user.cidade);
              const cidadeSnapshot = await getDoc(cidadeRef);
              user.cidade = cidadeSnapshot.exists() ? cidadeSnapshot.data().nome : 'N/A';
            }

            if (user.igreja) {
              const igrejaRef = doc(db, 'Igreja', user.igreja);
              const igrejaSnapshot = await getDoc(igrejaRef);
              user.igreja = igrejaSnapshot.exists() ? igrejaSnapshot.data().nome : 'N/A';
            }

            if (user.cargo) {
              const cargoRef = doc(db, 'Cargo', user.cargo);
              const cargoSnapshot = await getDoc(cargoRef);
              user.cargo = cargoSnapshot.exists() ? cargoSnapshot.data().nome : 'N/A';
            }

            return user;
          })
        );

        setUsers(userData);
        setLoading(false);
      } catch (error) {
        console.error('Erro ao buscar usuarios:', error);
        setLoading(false);
      }
    };

    getUserFromDatabase();
  }, []);

  useEffect(() => {
    async function loadPermissions() {
      const storedUser = localStorage.getItem('@userLogado');
      if (!storedUser) {
        console.error('Usuário não encontrado no localStorage.');
        return;
      }

      const userData = JSON.parse(storedUser);
      const cargoId = userData?.cargo;

      if (!cargoId) {
        console.error('ID do cargo não encontrado no usuário.');
        return;
      }

      try {
        const cargoRef = doc(db, 'Cargo', cargoId);
        const cargoDoc = await getDoc(cargoRef);

        if (cargoDoc.exists()) {
          const cargoData = cargoDoc.data();
          setPermissions(cargoData.permissoes || []);
        } else {
          console.error('Documento do cargo não encontrado no Firestore.');
        }
      } catch (error) {
        console.error('Erro ao buscar permissões no Firestore:', error);
      }
    }

    loadPermissions();
  }, []);

  const hasPermission = (permission) => {
    return permissions.includes(permission);
  };

  const handleDelete = async (id) => {
    try {
      const userRef = doc(db, 'User', id);
      await deleteDoc(userRef);
      toast.success('Usuario excluído com sucesso!');
      setUsers(users.filter(user => user.id !== id));
    } catch (error) {
      console.error('Erro ao excluir usuario:', error);
      toast.error('Erro ao excluir usuario.');
    }
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
  };

  const sortedUsers = React.useMemo(() => {
    let sortableUsers = [...users];
    if (sortConfig.key) {
      sortableUsers.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableUsers;
  }, [users, sortConfig]);

  const filteredUsers = sortedUsers.filter(user =>
    (!filterGenero || user.genero === filterGenero) &&
    (!filterCargo || user.cargo === filterCargo) &&
    (!filterCidade || user.cidade === filterCidade) &&
    (!filterIgreja || user.igreja === filterIgreja) &&
    user.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .includes(
        searchTerm
          .toLowerCase()
          .normalize("NFD")
          .replace(/[\u0300-\u036f]/g, "")
      )

  );

  const uniqueValues = (key) => [...new Set(users.map(user => user[key]).filter(Boolean))];

  return (
    <div>
      <Header />
      <div className="content">
        <div className="title-container">
          <div className="title-left">
            <FaAddressCard size={25} />
            <h1>Usuários</h1>
          </div>
          <Link to="/user/new" className="title-link incluir">
            <FiPlus color="#FFF" size={25} />
            <span>Incluir usuário</span>
          </Link>
        </div>

        <div className="filters">
          <input
            type="text"
            placeholder="Buscar por nome..."
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <select value={filterGenero} onChange={handleFilterChange(setFilterGenero)}>
            <option value="">Todos os Gêneros</option>
            {uniqueValues('genero').map(genero => (
              <option key={genero} value={genero}>{genero}</option>
            ))}
          </select>
          <select value={filterCargo} onChange={handleFilterChange(setFilterCargo)}>
            <option value="">Todos os Cargos</option>
            {uniqueValues('cargo').map(cargo => (
              <option key={cargo} value={cargo}>{cargo}</option>
            ))}
          </select>
          <select value={filterCidade} onChange={handleFilterChange(setFilterCidade)}>
            <option value="">Todas as Cidades</option>
            {uniqueValues('cidade').map(cidade => (
              <option key={cidade} value={cidade}>{cidade}</option>
            ))}
          </select>
          <select value={filterIgreja} onChange={handleFilterChange(setFilterIgreja)}>
            <option value="">Todas as Casas de Oração</option>
            {uniqueValues('igreja').map(igreja => (
              <option key={igreja} value={igreja}>{igreja}</option>
            ))}
          </select>
        </div>

        {loading ? (
          <span>Carregando usuarios...</span>
        ) : users.length === 0 ? (
          <span>Nenhum usuario encontrado...</span>
        ) : (
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('name')}>Nome</th>
                <th onClick={() => handleSort('genero')}>Gênero</th>
                <th onClick={() => handleSort('cargo')}>Cargo</th>
                <th onClick={() => handleSort('cidade')}>Cidade</th>
                <th onClick={() => handleSort('igreja')}>Casa de Oração</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr key={index}>
                  <td>{user.name}</td>
                  <td>{user.genero}</td>
                  <td>{user.cargo || 'N/A'}</td>
                  <td>{user.cidade || 'N/A'}</td>
                  <td>{user.igreja || 'N/A'}</td>
                  <td>
                    <Link to={`/user/edit/${user.id}`} className="action" style={{ backgroundColor: '#03c9e9' }}>
                      <FiEdit2 color='#FFF' size={17} />
                    </Link>
                    {hasPermission('deletar_itens') && (
                      <button
                        className="action"
                        style={{ backgroundColor: '#e90303' }}
                        onClick={() => handleDelete(user.id)}
                      >
                        <FaRegTrashAlt color="#FFF" size={17} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
